// src/components/Analysis.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import SaveReportModal from './SaveReportModal';
import ActionModal from './ActionModal';
import { useLanguage } from '../LanguageContext';

import { PieController } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, PieController);

// ── palette ──────────────────────────────────────────────────────────────────
const COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6',
    '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4',
    '#a855f7', '#84cc16', '#e11d48', '#0ea5e9', '#d946ef'
];

// ── date helpers ─────────────────────────────────────────────────────────────
function fmt(d) { return d.toISOString().slice(0, 10); }
function presetRange(preset) {
    const now = new Date();
    const to = fmt(now);
    if (preset === 'today') return { from: to, to };
    if (preset === 'week') {
        const d = new Date(now);
        d.setDate(d.getDate() - 6);
        return { from: fmt(d), to };
    }
    if (preset === 'month') {
        const d = new Date(now);
        d.setDate(d.getDate() - 29);
        return { from: fmt(d), to };
    }
    if (preset === 'year') {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        return { from: fmt(d), to };
    }
    return {};
}

// ── reusable styles ──────────────────────────────────────────────────────────
const card = {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: '20px 24px',
    marginBottom: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    position: 'relative',
    minHeight: 200,
};
const cardTitle = { fontSize: 15, fontWeight: 600, color: '#1a1c1d', marginBottom: 14 };
const presetBtn = (active) => ({
    padding: '5px 14px',
    borderRadius: 6,
    border: active ? '1px solid #6366f1' : '1px solid #d1d5db',
    background: active ? '#eef2ff' : '#fff',
    color: active ? '#4338ca' : '#374151',
    fontWeight: 500,
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all .15s',
});
const dateInput = {
    padding: '5px 10px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    fontSize: 12,
    color: '#374151',
    outline: 'none',
};

// ── DateRangeFilter sub-component ────────────────────────────────────────────
const DateRangeFilter = ({ range, setRange }) => {
    const { t } = useLanguage();
    const [preset, setPreset] = useState('month');
    useEffect(() => { setRange(presetRange('month')); }, []); // default
    const pick = (p) => { setPreset(p); setRange(presetRange(p)); };
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            {['today', 'week', 'month', 'year'].map(p => (
                <button key={p} style={presetBtn(preset === p)} onClick={() => pick(p)}>
                    {t[p] || (p.charAt(0).toUpperCase() + p.slice(1))}
                </button>
            ))}
            <span style={{ fontSize: 12, color: '#9ca3af' }}>|</span>
            <input type="date" style={dateInput} value={range.from || ''} onChange={e => { setPreset('custom'); setRange(r => ({ ...r, from: e.target.value })); }} />
            <span style={{ fontSize: 12, color: '#9ca3af' }}>–</span>
            <input type="date" style={dateInput} value={range.to || ''} onChange={e => { setPreset('custom'); setRange(r => ({ ...r, to: e.target.value })); }} />
        </div>
    );
};

// ── chart default options ────────────────────────────────────────────────────
const barOpts = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12 },
            bodyFont: { size: 12 },
            cornerRadius: 8,
            padding: 10,
        }
    },
    scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#6b7280' } },
        y: { grid: { color: '#f3f4f6' }, ticks: { font: { size: 11 }, color: '#6b7280' }, beginAtZero: true }
    }
});

const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: 'right', labels: { font: { size: 12 }, padding: 14, usePointStyle: true, pointStyle: 'circle' } },
        tooltip: { backgroundColor: '#1e293b', cornerRadius: 8, padding: 10, bodyFont: { size: 12 } }
    },
    cutout: '60%',
};

// ── FilterDropdown sub-component ──────────────────────────────────────────────
const FilterDropdown = ({ source, field, label, selectedValues, onChange }) => {
    const { t, isRtl } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [values, setValues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen && values.length === 0) {
            setLoading(true);
            axios.get(`${API_BASE}/analytics/field-values?source=${source}&field=${field}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })
                .then(r => setValues(r.data))
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }, [isOpen]);

    const filteredValues = values.filter(v => String(v).toLowerCase().includes(search.toLowerCase()));

    const toggleValue = (val) => {
        const next = selectedValues.includes(val)
            ? selectedValues.filter(v => v !== val)
            : [...selectedValues, val];
        onChange(next);
    };

    return (
        <div style={{ position: 'relative', width: '100%', direction: isRtl ? 'rtl' : 'ltr' }}>
            <div onClick={() => setIsOpen(!isOpen)} style={{
                padding: '2px 10px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff',
                fontSize: 11, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                minHeight: 28
            }}>
                <span style={{ color: selectedValues.length ? '#1a1c1d' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedValues.length ? `${selectedValues.length} ${t.selected}` : `${t.filter_label} ${label}...`}
                </span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6d7175" strokeWidth="3" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: isRtl ? 0 : 8, marginRight: isRtl ? 8 : 0 }}>
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </div>

            {isOpen && (
                <>
                    <div onClick={() => setIsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
                    <div className="fade-in" style={{
                        position: 'absolute', top: '100%', [isRtl ? 'right' : 'left']: 0, [isRtl ? 'left' : 'right']: 0, marginTop: 4, background: '#fff',
                        border: '1px solid #d2d2d2', borderRadius: 8, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                        zIndex: 2001, maxHeight: 250, display: 'flex', flexDirection: 'column', textAlign: isRtl ? 'right' : 'left'
                    }}>
                        <div style={{ padding: 8, borderBottom: '1px solid #f3f4f6', position: 'relative' }}>
                            <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 16, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            </span>
                            <input
                                autoFocus
                                type="text"
                                placeholder={t.search_values}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ width: '100%', padding: isRtl ? '6px 26px 6px 10px' : '6px 10px 6px 26px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, outline: 'none', boxSizing: 'border-box', textAlign: isRtl ? 'right' : 'left' }}
                            />
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
                            {loading ? <div style={{ padding: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>{t.loading_values}</div> : (
                                filteredValues.length ? filteredValues.map(v => (
                                    <div key={v} onClick={() => toggleValue(v)} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer',
                                        transition: 'background 0.1s', flexDirection: isRtl ? 'row-reverse' : 'row'
                                    }} onMouseOver={e => e.currentTarget.style.background = '#f9fafb'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                        <div style={{
                                            width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selectedValues.includes(v) ? '#1976d2' : '#d2d2d2'}`,
                                            background: selectedValues.includes(v) ? '#1976d2' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {selectedValues.includes(v) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4"><path d="M20 6L9 17l-5-5" /></svg>}
                                        </div>
                                        <span style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: isRtl ? 'right' : 'left' }}>{String(v)}</span>
                                    </div>
                                )) : <div style={{ padding: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>{t.no_values_found}</div>
                            )}
                        </div>
                        {selectedValues.length > 0 && (
                            <div style={{ padding: 8, borderTop: '1px solid #f3f4f6', textAlign: isRtl ? 'left' : 'right' }}>
                                <button onClick={() => onChange([])} style={{ fontSize: 11, fontWeight: 600, color: '#cf1322', background: 'none', border: 'none', cursor: 'pointer' }}>{t.clear_all}</button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════════════
const Analysis = () => {
    const { t, isRtl } = useLanguage();
    // ── state ──────────────────────────────────────────────────────────────────
    const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'builder'
    const [savedReports, setSavedReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [modalConfig, setModalConfig] = useState(null); // { type, title, message }

    // ── fetch helpers ──────────────────────────────────────────────────────────
    const qs = (obj) => Object.entries(obj).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');

    const handleDeleteReport = async () => {
        if (!reportToDelete) return;
        setIsDeleting(true);
        try {
            await axios.delete(`${API_BASE}/analytics/reports/${reportToDelete._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            fetchReports();
        } catch (err) {
            console.error('Delete error:', err);
            setModalConfig({ type: 'error', title: t.delete_failed, message: t.delete_error });
        } finally {
            setIsDeleting(false);
            setReportToDelete(null);
        }
    };

    const fetchReports = () => {
        setReportsLoading(true);
        axios.get(`${API_BASE}/analytics/reports`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
            .then(r => setSavedReports(r.data))
            .catch(console.error)
            .finally(() => setReportsLoading(false));
    };

    useEffect(() => {
        if (viewMode === 'dashboard') fetchReports();
    }, [viewMode]);


    // ── render ─────────────────────────────────────────────────────────────────

    return (
        <div className="fade-in" style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr' }}>
            <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1a1c1d', margin: 0 }}>{t.analysis}</h1>
                    <p style={{ fontSize: 13, color: '#6d7175', marginTop: 4 }}>
                        {viewMode === 'dashboard' ? t.analysis_overview : t.builder_desc}
                    </p>
                </div>
                {viewMode === 'dashboard' && (
                    <button
                        onClick={() => setViewMode('builder')}
                        style={{
                            padding: '10px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff',
                            fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                            display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s', flexDirection: isRtl ? 'row-reverse' : 'row'
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 20v-8m0 0V4m0 8h8m-8 0H4" /></svg>
                        {t.custom_report_builder}
                    </button>
                )}
            </div>

            {viewMode === 'dashboard' ? (
                <>
                    {/* ── row 0: Saved Reports (Priority) ────────────────────────────────── */}
                    {savedReports.length > 0 && (
                        <div style={{ marginTop: 0, marginBottom: 40 }}>
                            <div style={{ ...cardTitle, display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #eee', paddingBottom: 15, marginBottom: 25, flexDirection: isRtl ? 'row-reverse' : 'row', textAlign: isRtl ? 'right' : 'left' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                                {t.saved_analysis_reports}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
                                {savedReports.map(report => (
                                    <SavedReportCard
                                        key={report._id}
                                        report={report}
                                        onEdit={() => {
                                            setEditingReport(report);
                                            setViewMode('builder');
                                        }}
                                        onDelete={() => setReportToDelete(report)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}


                    {reportToDelete && (
                        <DeleteConfirmation
                            itemName={reportToDelete.name}
                            isDeleting={isDeleting}
                            onConfirm={handleDeleteReport}
                            onCancel={() => setReportToDelete(null)}
                        />
                    )}

                    {modalConfig && (
                        <ActionModal
                            type={modalConfig.type}
                            title={modalConfig.title}
                            message={modalConfig.message}
                            onConfirm={() => setModalConfig(null)}
                        />
                    )}
                </>
            ) : (
                <DynamicReportBuilder
                    onBack={() => { setViewMode('dashboard'); setEditingReport(null); }}
                    onSaveSuccess={() => { fetchReports(); setViewMode('dashboard'); setEditingReport(null); }}
                    initialReport={editingReport}
                />
            )}
        </div>
    );
};

const Empty = () => {
    const { t } = useLanguage();
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: 13 }}>
            {t.no_data_range}
        </div>
    );
};

const DynamicReportBuilder = ({ onBack, onSaveSuccess, initialReport }) => {
    const { t, isRtl } = useLanguage();
    const [schema, setSchema] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [buckets, setBuckets] = useState(initialReport?.buckets || {
        axis: { source: '', field: '', label: t.select_axis },
        values: [] // each: {id, source, field, label, customName, color, multiplier: 1 }
    });
    const [filters, setFilters] = useState(initialReport?.filters || []); // each: { source, field, label, values: [] }
    const [activeAdjustId, setActiveAdjustId] = useState(null); // for popover
    const [chartType, setChartType] = useState(initialReport?.chartType || 'bar');
    const [dateFrom, setDateFrom] = useState(initialReport?.dateFrom || '');
    const [dateTo, setDateTo] = useState(initialReport?.dateTo || '');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [schemaLoading, setSchemaLoading] = useState(true);
    const [error, setError] = useState('');
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [modalConfig, setModalConfig] = useState(null);

    useEffect(() => {
        setSchemaLoading(true);
        axios.get(`${API_BASE}/analytics/dynamic-schema`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(r => {
            setSchema(r.data);
            // Don't auto-expand first source as per user request
        })
            .catch(console.error)
            .finally(() => setSchemaLoading(false));
    }, []);

    // Instant Updates: Run report when filters change
    useEffect(() => {
        if (buckets.axis.field && buckets.values.length > 0) {
            handleRun();
        }
    }, [filters]);

    const handleSaveReport = (name) => {
        setLoading(true);
        setShowSaveModal(false);
        const isEditing = !!initialReport?._id;
        const method = isEditing ? 'put' : 'post';
        const url = isEditing ? `${API_BASE}/analytics/reports/${initialReport._id}` : `${API_BASE}/analytics/reports`;

        axios[method](url, {
            name,
            buckets,
            chartType,
            dateFrom,
            dateTo,
            filters
        }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
            .then(() => {
                setModalConfig({
                    type: 'success',
                    message: isEditing ? t.report_updated : t.report_saved
                });
                setTimeout(() => {
                    if (onSaveSuccess) onSaveSuccess();
                }, 1500);
            })
            .catch(err => {
                console.error('Save/Update report error:', err);
                setModalConfig({
                    type: 'error',
                    title: t.save_failed,
                    message: t.save_error
                });
            })
            .finally(() => setLoading(false));
    };

    const handleAddField = (source, fieldObj, type) => {
        if (type === 'group') {
            setBuckets(prev => ({
                ...prev,
                axis: { source, field: fieldObj.key, label: t[fieldObj.key] || fieldObj.label }
            }));
        } else {
            const color = COLORS[buckets.values.length % COLORS.length];
            setBuckets(prev => ({
                ...prev,
                values: [...prev.values, {
                    id: Date.now(),
                    source,
                    field: fieldObj.key,
                    label: t[fieldObj.key] || fieldObj.label,
                    sourceLabel: schema[source].label,
                    customName: '',
                    color: color,
                    multiplier: 1,
                    aggregation: fieldObj.type === 'number' ? 'sum' : 'count'
                }]
            }));
        }
    };

    const updateValue = (id, updates) => {
        setBuckets(prev => ({
            ...prev,
            values: prev.values.map(v => v.id === id ? { ...v, ...updates } : v)
        }));
    };

    const removeValue = (id) => {
        setBuckets(prev => ({ ...prev, values: prev.values.filter(v => v.id !== id) }));
        if (activeAdjustId === id) setActiveAdjustId(null);
    };

    const handleClear = () => {
        setBuckets({
            axis: { source: '', field: '', label: t.select_axis || 'Select Axis' },
            values: []
        });
        setResults(null);
        setError('');
        setActiveAdjustId(null);
    };

    const handleRun = async () => {
        if (!buckets.axis.field || buckets.values.length === 0) {
            setError(t.builder_error || 'Incomplete Configuration');
            return;
        }

        const queries = buckets.values.map(v => ({
            source: v.source,
            groupBy: buckets.axis.field,
            metric: v.field,
            aggregation: v.aggregation,
            name: v.customName || undefined
        }));

        setLoading(true);
        setError('');
        setResults(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE}/analytics/dynamic`,
                { queries, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, globalFilters: filters },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Apply multiplier manually if needed on frontend (or backend could do it, but frontend is safer for this)
            const processed = res.data.map((series, idx) => {
                const bucketConfig = buckets.values[idx];
                if (series.data && bucketConfig.multiplier !== 1) {
                    return {
                        ...series,
                        data: series.data.map(d => ({ ...d, value: d.value * bucketConfig.multiplier }))
                    };
                }
                return series;
            });

            setResults(processed);
        } catch (err) {
            setError(err.response?.data?.error || t.fetch_failed || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Chart Data Building logic (mostly same as before but adapted for buckets)
    let chartData = null;
    if (results && results.length > 0) {
        const allLabels = new Set();
        results.forEach(series => {
            if (series.data) {
                series.data.forEach(d => allLabels.add(d.label));
            }
        });
        const labels = Array.from(allLabels).sort();
        const datasets = results.map((series, idx) => {
            const config = buckets.values[idx];
            const dataMap = {};
            if (series.data) {
                series.data.forEach(d => dataMap[d.label] = d.value);
            }
            const baseColor = config?.color || COLORS[idx % COLORS.length];
            const isSingleSeriesPie = (chartType === 'pie' || chartType === 'doughnut') && results.length === 1;
            return {
                label: config?.customName || (series.name || config?.label),
                data: labels.map(l => {
                    const val = dataMap[l];
                    if (val === undefined) return 0;
                    // Array = distinctCount result → plot the count, show strings in tooltip/table
                    if (Array.isArray(val)) return val.length;
                    return typeof val === 'number' ? val : 0;
                }),
                backgroundColor: isSingleSeriesPie ? labels.map((_, i) => COLORS[i % COLORS.length] + 'ee') : baseColor + (chartType === 'line' ? '33' : 'cc'),
                borderColor: baseColor,
                borderWidth: chartType === 'line' ? 2 : (chartType === 'bar' || chartType === 'stackedBar' ? 0 : 1),
                fill: chartType === 'line' ? false : undefined,
                tension: 0.4,
                borderRadius: chartType === 'bar' || chartType === 'stackedBar' ? 4 : undefined,
                barPercentage: 0.6,
                pointRadius: chartType === 'line' ? 4 : undefined,
            };
        });
        chartData = { labels, datasets };
    }

    const sharedOpts = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: chartType === 'pie' || chartType === 'doughnut' ? 'right' : 'top', labels: { font: { size: 11 }, padding: 12, usePointStyle: true, pointStyle: 'circle' } },
            tooltip: {
                backgroundColor: '#1e293b',
                cornerRadius: 8,
                padding: 10,
                bodyFont: { size: 12 },
                mode: (chartType === 'pie' || chartType === 'doughnut') ? 'nearest' : 'index',
                intersect: (chartType === 'pie' || chartType === 'doughnut') ? true : false,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';

                        // Find the original data point from results to get potential string values
                        const seriesIndex = context.datasetIndex;
                        const dataPoint = results[seriesIndex]?.data?.find(d => d.label === context.label);
                        const val = dataPoint ? dataPoint.value : context.raw;

                        if (typeof val === 'number') {
                            label += val.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 });
                        } else {
                            label += val;
                        }
                        return label;
                    }
                }
            },
        },
        ...(chartType === 'bar' || chartType === 'stackedBar' || chartType === 'line' ? {
            scales: {
                x: {
                    stacked: chartType === 'stackedBar',
                    grid: { display: false },
                    ticks: { font: { size: 10 }, color: '#6b7280', maxRotation: 40 }
                },
                y: {
                    stacked: chartType === 'stackedBar',
                    grid: { color: '#f3f4f6' },
                    ticks: {
                        font: { size: 10 }, color: '#6b7280',
                        callback: function (value) { return value.toLocaleString(isRtl ? 'ar-EG' : undefined); }
                    },
                    beginAtZero: true
                },
            },
            interaction: { mode: 'index', intersect: false }
        } : { cutout: chartType === 'doughnut' ? '60%' : undefined }),
    };

    const renderChart = () => {
        if (!chartData) return null;
        if (chartType === 'bar' || chartType === 'stackedBar') return <Bar data={chartData} options={sharedOpts} />;
        if (chartType === 'line') return <Line data={chartData} options={sharedOpts} />;
        if (chartType === 'pie') return <Pie data={chartData} options={sharedOpts} />;
        if (chartType === 'doughnut') return <Doughnut data={chartData} options={sharedOpts} />;
        return null;
    };

    return (
        <div className="fade-in" style={{ marginTop: 40 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, justifyContent: 'space-between', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 4, height: 28, borderRadius: 4, background: '#1976d2' }} />
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1c1d' }}>{t.report_builder}</h2>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#f6f6f7', border: '1px solid #d2d2d2', color: '#6d7175' }}>PRO</span>
                </div>
                <button
                    onClick={onBack}
                    style={{
                        padding: '6px 12px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', color: '#6d7175',
                        fontWeight: 600, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s',
                        flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseOut={e => e.currentTarget.style.background = '#fff'}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    {t.back_to_graphs}
                </button>
            </div>

            <div style={{
                display: 'grid', gridTemplateColumns: isRtl ? 'minmax(300px, 1fr) 3fr minmax(240px, 1fr)' : 'minmax(240px, 1fr) 3fr minmax(300px, 1fr)',
                background: '#fff', borderRadius: 12, border: '1px solid #ebebeb', boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                minHeight: 700, overflow: 'hidden', direction: isRtl ? 'rtl' : 'ltr'
            }}>
                {/* 1. Fields Panel */}
                <div style={{ background: '#f9fafb', [isRtl ? 'borderLeft' : 'borderRight']: '1px solid #ebebeb', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '16px 20px', fontSize: 13, fontWeight: 600, color: '#1a1c1d', borderBottom: '1px solid #ebebeb', textAlign: isRtl ? 'right' : 'left' }}>{t.data_sources}</div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
                        {schemaLoading ? (
                            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <Loading message={t.loading_sources} />
                            </div>
                        ) : (
                            schema && Object.entries(schema).map(([k, v]) => (
                                <div key={k} style={{ marginBottom: 12 }}>
                                    <div onClick={() => setSelectedSource(selectedSource === k ? null : k)} style={{
                                        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 8px',
                                        borderRadius: 6, background: selectedSource === k ? '#eef4ff' : 'transparent',
                                        color: selectedSource === k ? '#2c6ecb' : '#1a1c1d', fontWeight: 500, fontSize: 13
                                    }}>
                                        <svg width="14" height="14" viewBox="0 0 20 20" style={{ transform: selectedSource === k ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}><path d="M7 5l5 5-5 5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>
                                        {v.label}
                                    </div>
                                    {selectedSource === k && (
                                        <div style={{ marginLeft: 22, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {v.fields.map(f => (
                                                <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', borderRadius: 4 }} onMouseOver={e => e.currentTarget.style.background = '#f1f1f1'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                                    <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        <span style={{ color: f.type === 'number' ? '#6366f1' : (f.type === 'date' ? '#1976d2' : '#6d7175'), fontSize: 10, fontWeight: 700, width: 20 }}>
                                                            {f.type === 'number' ? '123' : (f.type === 'date' ? '📅' : 'Abc')}
                                                        </span>
                                                        {f.label}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        <button title="Set on X-Axis" onClick={() => handleAddField(k, f, 'group')} style={{ border: '1px solid #d2d2d2', background: buckets.axis.field === f.key && buckets.axis.source === k ? '#1976d2' : '#fff', color: buckets.axis.field === f.key && buckets.axis.source === k ? '#fff' : '#6d7175', cursor: 'pointer', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>X</button>
                                                        <button title="Add to Y-Axis" onClick={() => handleAddField(k, f, 'metric')} style={{
                                                            border: '1px solid #d2d2d2',
                                                            background: buckets.values.some(v => v.source === k && v.field === f.key) ? '#6366f1' : '#fff',
                                                            color: buckets.values.some(v => v.source === k && v.field === f.key) ? '#fff' : '#6366f1',
                                                            cursor: 'pointer', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700
                                                        }}>Y</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* 2. Visualization Canvas */}
                <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRight: isRtl ? '1px solid #ebebeb' : 'none', borderLeft: isRtl ? 'none' : '1px solid #ebebeb' }}>
                    {/* Canvas Header/Filter */}
                    <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ebebeb', background: '#fff', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: isRtl ? 'right' : 'left' }}>{t.date_range}</div>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#f6f6f7', borderRadius: 6, border: '1px solid #d2d2d2', overflow: 'hidden', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...dateInput, border: 'none', background: 'none', height: 28, padding: '0 8px', textAlign: isRtl ? 'right' : 'left' }} />
                                    <div style={{ width: 1, height: 16, background: '#d2d2d2' }} />
                                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...dateInput, border: 'none', background: 'none', height: 28, padding: '0 8px', textAlign: isRtl ? 'right' : 'left' }} />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <button onClick={handleClear} disabled={loading} style={{
                                padding: '6px 12px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', color: '#6d7175', fontWeight: 600, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer'
                            }}>
                                {t.new_report}
                            </button>
                            <div style={{ width: 1, height: 20, background: '#ebebeb', margin: '0 4px' }} />
                            <button onClick={handleRun} disabled={loading} style={{
                                padding: '6px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(25,118,210,0.1)'
                            }}>
                                {loading ? t.loading : t.run_report}
                            </button>
                            <button onClick={() => {
                                if (!buckets.axis.field || !buckets.values.length) {
                                    setModalConfig({ type: 'error', title: t.incomplete_config, message: t.builder_error });
                                    return;
                                }
                                setShowSaveModal(true);
                            }} disabled={loading || !results} style={{
                                padding: '6px 14px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 600, fontSize: 12, cursor: (loading || !results) ? 'not-allowed' : 'pointer', transition: 'all 0.2s', opacity: (loading || !results) ? 0.6 : 1
                            }}>
                                {t.save}
                            </button>
                        </div>
                    </div>

                    {showSaveModal && (
                        <SaveReportModal
                            isSaving={loading}
                            initialName={initialReport?.name}
                            onSave={handleSaveReport}
                            onCancel={() => setShowSaveModal(false)}
                        />
                    )}

                    {modalConfig && (
                        <ActionModal
                            type={modalConfig.type}
                            title={modalConfig.title}
                            message={modalConfig.message}
                            onConfirm={() => setModalConfig(null)}
                        />
                    )}

                    {/* Horizontal Filters Area */}
                    {(buckets.axis.field || buckets.values.length > 0) && (
                        <div className="fade-in" style={{
                            padding: '6px 14px',
                            background: '#fcfcfc',
                            borderBottom: '1px solid #ebebeb',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 8,
                            alignItems: 'flex-end',
                            zIndex: 10
                        }}>

                            {/* Axis Filter */}
                            {buckets.axis.field && (
                                <div style={{ minWidth: 120, flex: 1, maxWidth: 200 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{buckets.axis.label}</div>
                                    <FilterDropdown
                                        source={buckets.axis.source}
                                        field={buckets.axis.field}
                                        label={buckets.axis.label}
                                        selectedValues={filters.find(f => f.source === buckets.axis.source && f.field === buckets.axis.field)?.values || []}
                                        onChange={(vals) => {
                                            setFilters(prev => {
                                                const existing = prev.find(f => f.source === buckets.axis.source && f.field === buckets.axis.field);
                                                if (existing) {
                                                    return prev.map(f => f === existing ? { ...f, values: vals } : f).filter(f => f.values.length > 0);
                                                }
                                                return [...prev, { source: buckets.axis.source, field: buckets.axis.field, label: buckets.axis.label, values: vals }];
                                            });
                                        }}
                                    />
                                </div>
                            )}

                            {/* Value Filters — deduplicated */}
                            {(() => {
                                const rendered = new Set();
                                // Mark the axis as already rendered if it exists
                                if (buckets.axis.field) rendered.add(`${buckets.axis.source}:${buckets.axis.field}`);
                                return buckets.values.map(v => {
                                    const key = `${v.source}:${v.field}`;
                                    if (rendered.has(key)) return null;
                                    rendered.add(key);
                                    return (
                                        <div key={v.id} style={{ minWidth: 120, flex: 1, maxWidth: 200 }}>
                                            <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{v.customName || v.label}</div>
                                            <FilterDropdown
                                                source={v.source}
                                                field={v.field}
                                                label={v.label}
                                                selectedValues={filters.find(f => f.source === v.source && f.field === v.field)?.values || []}
                                                onChange={(vals) => {
                                                    setFilters(prev => {
                                                        const existing = prev.find(f => f.source === v.source && f.field === v.field);
                                                        if (existing) {
                                                            return prev.map(f => f === existing ? { ...f, values: vals } : f).filter(f => f.values.length > 0);
                                                        }
                                                        return [...prev, { source: v.source, field: v.field, label: v.label, values: vals }];
                                                    });
                                                }}
                                            />
                                        </div>
                                    );
                                });
                            })()}

                            {filters.length > 0 && (
                                    <button
                                        onClick={() => setFilters([])}
                                        style={{
                                            fontSize: 10, fontWeight: 700, color: '#cf1322', background: 'none', border: 'none',
                                            cursor: 'pointer', paddingBottom: 8, [isRtl ? 'marginRight' : 'marginLeft']: 'auto', alignSelf: 'flex-end'
                                        }}
                                    >
                                        {t.clear_all}
                                    </button>
                            )}
                        </div>
                    )}

                    {/* Chart Frame */}
                    <div style={{ flex: 1, padding: 32, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        {error && <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322', padding: '10px 16px', borderRadius: 6, marginBottom: 20, fontSize: 13 }}>{error}</div>}
                        {!results && !loading && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#6d7175', border: '2px dashed #ebebeb', borderRadius: 12, textAlign: 'center' }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: 16, opacity: 0.5 }}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 15h18M9 3v18" /></svg>
                                <p style={{ margin: 0, fontSize: 14 }}>{t.welcome_canvas}</p>
                                <p style={{ margin: '4px 0 0', fontSize: 12 }}>{t.pick_axis_value}</p>
                            </div>
                        )}
                        {loading && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loading message={t.analyzing_sources} /></div>}
                        {results && (
                            <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ marginBottom: 20, textAlign: 'center' }}>
                                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a1c1d' }}>
                                        {results.map((s, idx) => (buckets.values[idx]?.customName || s.name)).join(' & ')}
                                        <span style={{ color: '#6d7175', fontWeight: 400, margin: '0 8px' }}>{t.by}</span>
                                        {buckets.axis.label}
                                    </h3>
                                </div>
                                <div style={{ flex: 1, minHeight: 400 }}>{renderChart()}</div>
                                <div style={{ marginTop: 40, border: '1px solid #ebebeb', borderRadius: 8, overflow: 'hidden' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb', height: 40 }}>
                                                <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '0 16px', fontWeight: 600, borderBottom: '1px solid #ebebeb' }}>{buckets.axis.label}</th>
                                                {results.map((s, idx) => {
                                                    const config = buckets.values[idx];
                                                    return <th key={idx} style={{ textAlign: isRtl ? 'left' : 'right', padding: '0 16px', fontWeight: 600, borderBottom: '1px solid #ebebeb' }}>
                                                        <span style={{ color: config?.color, [isRtl ? 'marginLeft' : 'marginRight']: 6 }}>●</span>
                                                        {config?.customName || s.name}
                                                    </th>;
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chartData.labels.map((lbl, idx) => (
                                                <tr key={idx} style={{ height: 36, borderBottom: '1px solid #f1f1f1' }}>
                                                    <td style={{ padding: '0 16px', fontWeight: 500, textAlign: isRtl ? 'right' : 'left' }}>{lbl}</td>
                                                    {results.map((s, sIdx) => {
                                                        const p = s.data?.find(d => d.label === lbl);
                                                        return <td key={sIdx} style={{ textAlign: isRtl ? 'left' : 'right', padding: '0 16px', color: '#6d7175' }}>{p ? (Array.isArray(p.value) ? p.value.join(', ') : (typeof p.value === 'number' ? p.value.toLocaleString() : p.value)) : '-'}</td>;
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. Configuration Panel */}
                <div style={{
                    background: '#f9fafb',
                    [isRtl ? 'borderRight' : 'borderLeft']: '1px solid #ebebeb',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 20,
                    maxHeight: 700,
                    overflowY: 'auto',
                    textAlign: isRtl ? 'right' : 'left'
                }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1c1d', marginBottom: 16 }}>{t.visualizations}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 24 }}>
                        {[
                            { key: 'bar', icon: 'M4 20h16M7 16V8M12 16V4M17 16V10', label: 'Bar' },
                            { key: 'stackedBar', icon: 'M4 20h16M7 16V11M7 11V6M12 16V12M12 12V8M17 16V13M17 13V10', label: 'Stacked Bar' },
                            { key: 'line', icon: 'M4 16 L8 12 L12 14 L18 6', label: 'Line' },
                            { key: 'pie', icon: 'M12 2A10 10 0 1022 12A10 10 0 0012 2zM12 2v10h10', label: 'Pie' },
                            { key: 'doughnut', icon: 'M12 2A10 10 0 1022 12A10 10 0 0012 2zM12 6A6 6 0 1018 12A6 6 0 0012 6z', label: 'Doughnut' }
                        ].map(t => (
                            <button key={t.key} onClick={() => setChartType(t.key)} title={t.label} style={{
                                width: '100%', aspectRatio: '1', borderRadius: 6, border: `1.5px solid ${chartType === t.key ? '#1976d2' : '#d2d2d2'}`,
                                background: chartType === t.key ? '#fff' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: chartType === t.key ? '#1976d2' : '#6d7175',
                                transition: 'all 0.15s'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={t.icon} /></svg>
                            </button>
                        ))}
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>{t.data_mapping}</div>

                    {/* Axis Bucket */}
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6d7175" strokeWidth="2"><path d="M3 3v18h18" /></svg>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1c1d' }}>{t.axis_shared}</div>
                        </div>
                        <div style={{
                            padding: '12px', border: buckets.axis.field ? '1px solid #1976d2' : '1.5px dashed #d2d2d2',
                            borderRadius: 8, background: buckets.axis.field ? '#f6ffed' : '#fff', fontSize: 13,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 44,
                            transition: 'all 0.2s'
                        }}>
                            <span style={{ color: buckets.axis.field ? '#1a1c1d' : '#9ca3af', fontWeight: buckets.axis.field ? 500 : 400 }}>
                                {buckets.axis.field ? buckets.axis.label : t.select_grouping}
                            </span>
                            {buckets.axis.field && (
                                <button onClick={() => setBuckets(prev => ({ ...prev, axis: { source: '', field: '', label: 'Select Axis' } }))}
                                    style={{ border: 'none', background: 'none', color: '#6d7175', cursor: 'pointer', fontSize: 18 }}>&times;</button>
                            )}
                        </div>
                    </div>

                    {/* Values Bucket */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M12 20v-8m0 0V4m0 8h8m-8 0H4" /></svg>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1c1d' }}>{t.values_metrics}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {buckets.values.map((v, vIdx) => (
                                <div key={v.id} style={{ position: 'relative' }}>
                                    <div className="fade-in" style={{
                                        padding: '10px 12px', border: '1px solid #d2d2d2',
                                        borderLeft: `5px solid ${v.color}`,
                                        borderRadius: '4px 8px 8px 4px', background: '#fff', fontSize: 12,
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                        borderBottom: activeAdjustId === v.id ? 'none' : '1px solid #d2d2d2',
                                        borderRadius: activeAdjustId === v.id ? '4px 8px 0 0' : '4px 8px 8px 4px'
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontWeight: 600, color: '#1a1c1d' }}>{v.customName || v.label}</span>
                                            <span style={{ fontSize: 10, color: '#6d7175' }}>{v.sourceLabel} • {v.aggregation}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            {v.multiplier !== 1 && <span style={{ fontSize: 10, background: '#eef2ff', color: '#6366f1', padding: '1px 4px', borderRadius: 3 }}>x{v.multiplier}</span>}
                                            <button onClick={() => setActiveAdjustId(activeAdjustId === v.id ? null : v.id)} style={{ border: 'none', background: '#f6f6f7', color: '#6d7175', cursor: 'pointer', borderRadius: 4, padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#ebebeb'} onMouseOut={e => e.currentTarget.style.background = '#f6f6f7'}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ transform: (activeAdjustId === v.id) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); removeValue(v.id); }} style={{ border: 'none', background: 'none', color: '#cf1322', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>&times;</button>
                                        </div>
                                    </div>

                                    {/* Adjustment Popover */}
                                    {activeAdjustId === v.id && (
                                        <div style={{
                                            padding: '12px', background: '#fff', border: '1px solid #d2d2d2', borderTop: 'none',
                                            borderRadius: '0 0 8px 8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                            zIndex: 10, display: 'flex', flexDirection: 'column', gap: 8
                                        }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, direction: isRtl ? 'rtl' : 'ltr' }}>
                                                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: '#6d7175', display: 'block', marginBottom: 4 }}>{t.rename}</label>
                                                    <input type="text" value={v.customName} onChange={(e) => updateValue(v.id, { customName: e.target.value })} placeholder="..." style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ebebeb', fontSize: 11, textAlign: isRtl ? 'right' : 'left' }} />
                                                </div>
                                                <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: '#6d7175', display: 'block', marginBottom: 4 }}>{t.aggregation}</label>
                                                    <select value={v.aggregation} onChange={(e) => updateValue(v.id, { aggregation: e.target.value })} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ebebeb', fontSize: 11, textAlign: isRtl ? 'right' : 'left' }}>
                                                        <option value="sum">{t.sum}</option>
                                                        <option value="avg">{t.avg}</option>
                                                        <option value="count">{t.count}</option>
                                                        <option value="distinctCount">{t.distinct_count}</option>
                                                        <option value="list">{t.list_distinct}</option>
                                                        <option value="min">{t.min}</option>
                                                        <option value="max">{t.max}</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 10, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                                                <div style={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: '#6d7175', display: 'block', marginBottom: 4 }}>{t.color}</label>
                                                    <input type="color" value={v.color} onChange={(e) => updateValue(v.id, { color: e.target.value })} style={{ width: '100%', height: 24, border: 'none', padding: 0, cursor: 'pointer', background: 'none' }} />
                                                </div>
                                                <div style={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: '#6d7175', display: 'block', marginBottom: 4 }}>{t.multiplier}</label>
                                                    <input type="number" value={v.multiplier} onChange={(e) => updateValue(v.id, { multiplier: parseFloat(e.target.value) || 1 })} step="0.01" style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ebebeb', fontSize: 11, textAlign: isRtl ? 'right' : 'left' }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {buckets.values.length === 0 && (
                                <div style={{
                                    padding: '24px 16px', border: '1.5px dashed #d2d2d2', borderRadius: 8,
                                    textAlign: 'center', fontSize: 12, color: '#9ca3af', background: '#fff'
                                }}>
                                    <div style={{ fontSize: 20, marginBottom: 4 }}>∑</div>
                                    {t.add_metrics_compare}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analysis;

const SavedReportCard = ({ report, onDelete, onEdit }) => {
    const { t, isRtl } = useLanguage();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showTable, setShowTable] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]); // Start empty on reload
    const [localDateFrom, setLocalDateFrom] = useState(''); // Start empty on reload
    const [localDateTo, setLocalDateTo] = useState(''); // Start empty on reload
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

    const fetchFilteredData = (filters, dateFrom, dateTo) => {
        setLoading(true);
        const queries = report.buckets.values.map(v => ({
            source: v.source,
            groupBy: report.buckets.axis.field,
            metric: v.field,
            aggregation: v.aggregation || 'sum',
            name: v.customName || v.label
        }));

        axios.post(`${API_BASE}/analytics/dynamic`, {
            queries,
            dateFrom,
            dateTo,
            globalFilters: filters
        }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
            .then(r => setResults(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchFilteredData(activeFilters, localDateFrom, localDateTo);
    }, [report, activeFilters, localDateFrom, localDateTo]);

    const chartData = useMemo(() => {
        if (!results || !results.length) return null;
        const allLabels = new Set();
        results.forEach(series => series.data?.forEach(d => allLabels.add(d.label)));
        const labels = Array.from(allLabels).sort();

        const datasets = results.map((series, idx) => {
            const config = report.buckets.values[idx];
            const dataMap = {};
            series.data?.forEach(d => dataMap[d.label] = d.value);
            const baseColor = config.color || COLORS[idx % COLORS.length];
            const isSingleSeriesPie = (report.chartType === 'pie' || report.chartType === 'doughnut') && results.length === 1;
            return {
                label: config.customName || config.label,
                data: labels.map(l => {
                    const val = dataMap[l];
                    if (val === undefined) return 0;
                    return typeof val === 'number' ? val : 0;
                }),
                backgroundColor: isSingleSeriesPie ? labels.map((_, i) => COLORS[i % COLORS.length] + 'ee') : baseColor + (report.chartType === 'line' ? '33' : 'cc'),
                borderColor: baseColor,
                borderWidth: report.chartType === 'line' ? 2 : (report.chartType === 'bar' || report.chartType === 'stackedBar' ? 0 : 1),
                fill: report.chartType === 'line' ? false : undefined,
                tension: 0.4,
                borderRadius: (report.chartType === 'bar' || report.chartType === 'stackedBar') ? 4 : undefined,
                barPercentage: 0.6,
                pointRadius: report.chartType === 'line' ? 4 : undefined,
                pointHitRadius: report.chartType === 'line' ? 10 : undefined,
            };
        });
        return { labels, datasets };
    }, [results, report]);

    const options = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: 'top', labels: { boxWidth: 10, font: { size: 10 } } },
            tooltip: {
                backgroundColor: '#1e293b',
                cornerRadius: 8,
                padding: 10,
                bodyFont: { size: 12 },
                mode: (report.chartType === 'pie' || report.chartType === 'doughnut') ? 'nearest' : 'index',
                intersect: (report.chartType === 'pie' || report.chartType === 'doughnut') ? true : false,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';

                        const seriesIndex = context.datasetIndex;
                        const dataPoint = results[seriesIndex]?.data?.find(d => d.label === context.label);
                        const val = dataPoint ? dataPoint.value : context.raw;

                        if (typeof val === 'number') {
                            label += val.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 });
                        } else {
                            label += val;
                        }
                        return label;
                    }
                }
            },
        },
        interaction: {
            mode: (report.chartType === 'pie' || report.chartType === 'doughnut') ? 'nearest' : 'index',
            intersect: (report.chartType === 'pie' || report.chartType === 'doughnut') ? true : false
        },
        scales: (report.chartType === 'bar' || report.chartType === 'stackedBar' || report.chartType === 'line') ? {
            x: {
                stacked: report.chartType === 'stackedBar',
                grid: { display: false },
                ticks: { font: { size: 10 }, maxRotation: 40 }
            },
            y: {
                stacked: report.chartType === 'stackedBar',
                grid: { color: '#f3f4f6' },
                ticks: {
                    font: { size: 10 },
                    callback: function (value) { return value.toLocaleString(isRtl ? 'ar-EG' : undefined); }
                },
                beginAtZero: true
            }
        } : (report.chartType === 'doughnut' ? { cutout: '60%' } : {})
    };

    return (
        <div className="fade-in" style={{ ...card, minHeight: 450, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={cardTitle}>{report.name}</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{ background: 'none', border: 'none', color: showTable ? '#1976d2' : '#6d7175', cursor: 'pointer', padding: 5 }}
                        title={showTable ? t.show_chart : t.show_table}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 9v12" /></svg>
                    </button>
                    <button
                        className="menu-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                            setMenuOpen(!menuOpen);
                        }}
                        style={{ background: 'none', border: 'none', color: '#6d7175', cursor: 'pointer', padding: 5 }}
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                    </button>

                    {menuOpen && (
                        <div style={{
                            position: 'fixed',
                            [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 130),
                            top: menuPos.y, background: '#fff',
                            borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', minWidth: 140, zIndex: 100,
                            border: '1px solid #ebebeb', padding: 4, display: 'flex', flexDirection: 'column',
                            textAlign: isRtl ? 'right' : 'left'
                        }}>
                            <button onClick={() => { setMenuOpen(false); onEdit(); }} style={{
                                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                                cursor: 'pointer', color: '#4b5563', fontSize: 12, borderRadius: 4
                            }} onMouseOver={e => e.target.style.background = '#f9fafb'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit_report}</button>
                            <button onClick={() => { setMenuOpen(false); onDelete(); }} style={{
                                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                                cursor: 'pointer', color: '#ef4444', fontSize: 12, borderRadius: 4
                            }} onMouseOver={e => e.target.style.background = '#f9fafb'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete_report}</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Interactive filters for saved reports */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 16,
                marginTop: 8,
                padding: '6px 14px',
                background: '#fcfcfc',
                borderRadius: 8,
                border: '1px solid #f1f1f1',
                alignItems: 'flex-end',
                flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
                {/* Default Month Filter */}
                <div style={{ minWidth: 120, flex: 1, maxWidth: 200, textAlign: isRtl ? 'right' : 'left' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{t.month}</div>
                    <FilterDropdown
                        source={report.buckets.axis.source}
                        field="month"
                        label={t.month}
                        selectedValues={activeFilters.find(f => f.field === 'month')?.values || []}
                        onChange={(vals) => {
                            setActiveFilters(prev => {
                                const existing = prev.find(f => f.field === 'month');
                                if (existing) {
                                    return prev.map(f => f === existing ? { ...f, values: vals } : f).filter(f => f.values.length > 0);
                                }
                                return [...prev, { source: report.buckets.axis.source, field: 'month', label: t.month || 'Month', values: vals }];
                            });
                        }}
                    />
                </div>

                {/* Default Date Range Filter */}
                <div style={{ minWidth: 200, flex: 1.5, maxWidth: 320, textAlign: isRtl ? 'right' : 'left' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{t.custom_date_range}</div>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 6, border: '1px solid #d2d2d2', overflow: 'hidden', height: 28, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                        <input type="date" value={localDateFrom} onChange={e => setLocalDateFrom(e.target.value)} style={{ border: 'none', background: 'none', height: '100%', padding: '0 8px', fontSize: 11, outline: 'none', width: '45%', textAlign: isRtl ? 'right' : 'left' }} />
                        <div style={{ width: 1, height: 16, background: '#d2d2d2' }} />
                        <input type="date" value={localDateTo} onChange={e => setLocalDateTo(e.target.value)} style={{ border: 'none', background: 'none', height: '100%', padding: '0 8px', fontSize: 11, outline: 'none', width: '45%', textAlign: isRtl ? 'right' : 'left' }} />
                    </div>
                </div>

                <div style={{ width: 1, height: 24, background: '#ebebeb', margin: '0 4px', marginBottom: 2 }} />

                {/* Axis Filter */}
                {report.buckets.axis.field && (
                    <div style={{ minWidth: 120, flex: 1, maxWidth: 200, textAlign: isRtl ? 'right' : 'left' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{report.buckets.axis.label}</div>
                        <FilterDropdown
                            source={report.buckets.axis.source}
                            field={report.buckets.axis.field}
                            label={report.buckets.axis.label}
                            selectedValues={activeFilters.find(f => f.source === report.buckets.axis.source && f.field === report.buckets.axis.field)?.values || []}
                            onChange={(vals) => {
                                setActiveFilters(prev => {
                                    const existing = prev.find(f => f.source === report.buckets.axis.source && f.field === report.buckets.axis.field);
                                    if (existing) {
                                        return prev.map(f => f === existing ? { ...f, values: vals } : f).filter(f => f.values.length > 0);
                                    }
                                    return [...prev, { source: report.buckets.axis.source, field: report.buckets.axis.field, label: report.buckets.axis.label, values: vals }];
                                });
                            }}
                        />
                    </div>
                )}

                {/* Metric Filters — deduplicated */}
                {(() => {
                    const rendered = new Set();
                    if (report.buckets.axis.field) rendered.add(`${report.buckets.axis.source}:${report.buckets.axis.field}`);
                    return report.buckets.values.map(v => {
                        const key = `${v.source}:${v.field}`;
                        if (rendered.has(key)) return null;
                        rendered.add(key);
                        return (
                            <div key={v.id} style={{ minWidth: 120, flex: 1, maxWidth: 200, textAlign: isRtl ? 'right' : 'left' }}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: '#6d7175', marginBottom: 2, textTransform: 'uppercase' }}>{v.customName || v.label}</div>
                                <FilterDropdown
                                    source={v.source}
                                    field={v.field}
                                    label={v.label}
                                    selectedValues={activeFilters.find(f => f.source === v.source && f.field === v.field)?.values || []}
                                    onChange={(vals) => {
                                        setActiveFilters(prev => {
                                            const existing = prev.find(f => f.source === v.source && f.field === v.field);
                                            if (existing) {
                                                return prev.map(f => f === existing ? { ...f, values: vals } : f).filter(f => f.values.length > 0);
                                            }
                                            return [...prev, { source: v.source, field: v.field, label: v.label, values: vals }];
                                        });
                                    }}
                                />
                            </div>
                        );
                    });
                })()}

                {(activeFilters.length > 0 || localDateFrom || localDateTo) && (
                    <button
                        onClick={() => {
                            setActiveFilters([]);
                            setLocalDateFrom('');
                            setLocalDateTo('');
                        }}
                        style={{
                            fontSize: 10, fontWeight: 700, color: '#cf1322', background: 'none', border: 'none',
                            cursor: 'pointer', paddingBottom: 8, [isRtl ? 'marginRight' : 'marginLeft']: 'auto'
                        }}
                    >
                        {t.clear_all}
                    </button>
                )}
            </div>

            <div style={{ height: 350, position: 'relative', overflowY: showTable ? 'auto' : 'visible' }}>
                {loading ? <Loading /> : (
                    chartData ? (
                        showTable ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb', height: 32, borderBottom: '1px solid #eee' }}>
                                        <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '0 8px' }}>{report.buckets.axis.label || t.axis}</th>
                                        {results.map((s, idx) => (
                                            <th key={idx} style={{ textAlign: isRtl ? 'left' : 'right', padding: '0 8px' }}>
                                                {report.buckets.values[idx]?.customName || s.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {chartData.labels.map((label, rowIdx) => (
                                        <tr key={label} style={{ height: 32, borderBottom: '1px solid #f9fafb' }}>
                                            <td style={{ padding: '0 8px', color: '#1a1c1d', fontWeight: 500 }}>{label}</td>
                                            {results.map((s, sIdx) => {
                                                const p = s.data?.find(d => d.label === label);
                                                const val = p ? p.value : '-';
                                                return (
                                                    <td key={sIdx} style={{ textAlign: isRtl ? 'left' : 'right', padding: '0 8px', color: '#4b5563' }}>
                                                        {typeof val === 'number' ? val.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 }) : val}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            report.chartType === 'pie' ? <Pie data={chartData} options={options} /> :
                                report.chartType === 'doughnut' ? <Doughnut data={chartData} options={options} /> :
                                    report.chartType === 'line' ? <Line data={chartData} options={options} /> :
                                        <Bar data={chartData} options={options} />
                        )
                    ) : <Empty />
                )}
            </div>
        </div>
    );
};
